import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { uploadRider, importRiderFromUrl, getRiderStatus, listRiders, getUploadQuota, createCheckoutSession, type UploadResponse } from "@/api/client";
import { mapLimit } from "@/lib/concurrency";
import {
  ADMIN_BATCH_SIZE,
  DEFAULT_BATCH_SIZE,
  QUOTA_GATE_TOAST,
  QUOTA_ITEM_ERROR,
  QUOTA_TOAST,
  computeBatchCap,
  isFreeQuotaExhausted,
  isQuotaError,
  uploadConcurrency,
} from "@/lib/uploadQuota";
import { useAuthStore } from "@/stores/auth";
import { useDemoWorkspaceStore } from "@/stores/demoWorkspace";
import { createDemoPdf } from "@/lib/demoWorkspace";
import { ONBOARDING_PROGRESS_QUERY_KEY } from "@/hooks/useOnboardingProgress";
import { Upload, FileText, X, Pencil, FolderOpen, AlertTriangle, Link as LinkIcon, Loader2, FileSearch, Sparkles, Gauge, CheckCircle2, Inbox, Lock, Zap } from "lucide-react";
import { toast } from "sonner";
import anime from "animejs";
import { ProcessingView } from "@/components/upload/ProcessingView";
import type { ProcessingItemData } from "@/components/upload/types";
import { Button, Input, SectionHeader, Icon } from "@/components/ds";
import { RidersWorkspaceHeader } from "@/components/riders/RidersWorkspaceHeader";

const EXTRACTION_STEPS = [
  {
    icon: FileSearch,
    title: "Read the document",
    desc: "Text and layout are parsed from the PDF — including scanned riders via OCR.",
  },
  {
    icon: Sparkles,
    title: "Extract structured data",
    desc: "Equipment, audio, power, hospitality and contacts are pulled into typed fields.",
  },
  {
    icon: Gauge,
    title: "Score confidence",
    desc: "Every field gets a confidence score, and the source line is linked for verification.",
  },
  {
    icon: CheckCircle2,
    title: "Ready to review",
    desc: "You confirm or correct, then approve and assign to a stage.",
  },
] as const;

interface UploadItem extends ProcessingItemData {
  file: File;
  result?: UploadResponse;
  contentHash?: string | null;
}

let nextId = 0;

function normalizeKey(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

async function fileSha256(file: File): Promise<string | null> {
  if (!globalThis.crypto?.subtle) return null;
  try {
    const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
    return [...new Uint8Array(digest)]
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}

export function RiderUploadPage() {
  const [items, setItems] = useState<UploadItem[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [showStaging, setShowStaging] = useState(true);
  const [showProcessing, setShowProcessing] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importArtistName, setImportArtistName] = useState("");
  const [importLoading, setImportLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const itemsRef = useRef<UploadItem[]>([]);
  const pollingRef = useRef(false);
  const stagingRef = useRef<HTMLDivElement>(null);
  const processingRef = useRef<HTMLDivElement>(null);
  const pdfPickerRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const demoActive = useDemoWorkspaceStore((s) => s.active && s.ownerId === user?.id);
  const isAdmin = !!user?.is_admin;

  // Keep ref in sync with state
  itemsRef.current = items;

  // Fetch existing riders for duplicate detection
  const { data: existingRiders } = useQuery({
    queryKey: ["riders"],
    queryFn: () => listRiders(),
  });

  // Fetch upload quota
  const { data: quota } = useQuery({
    queryKey: ["upload-quota"],
    queryFn: getUploadQuota,
  });

  // Free tier (v1.77.0): 10 riders / month, no overage — the only hard cap.
  // `quota.tier` is the effective tier, so a Free user inheriting Explorer
  // through an org is not gated here, and overage tiers are never gated nor
  // clamped client-side. The rules live in lib/uploadQuota.ts (unit-tested)
  // and are shared with the dashboard hero upload.
  const quotaExhausted = isFreeQuotaExhausted(quota, isAdmin);
  // Batch cap: admins get the wide cap and bypass the quota; Free is clamped
  // to what is left this month so a batch never half-fails server-side.
  const baseBatch = isAdmin ? ADMIN_BATCH_SIZE : DEFAULT_BATCH_SIZE;
  const maxBatch = computeBatchCap(quota, isAdmin, baseBatch);
  const batchClampedByQuota = maxBatch < baseBatch;

  const findDuplicate = (
    item: UploadItem,
  ): { kind: "artist" | "filename"; label: string } | null => {
    const lower = normalizeKey(item.artistName);
    const fileName = normalizeKey(item.fileName ?? item.file.name);
    if (!lower && !fileName) return null;
    for (const existing of existingRiders ?? []) {
      if (fileName && normalizeKey(existing.original_filename) === fileName) {
        return { kind: "filename", label: existing.original_filename ?? item.file.name };
      }
      if (lower && normalizeKey(existing.artist_name) === lower) {
        return { kind: "artist", label: existing.artist_name };
      }
    }
    return null;
  };

  // Poll for extraction status — reads from ref to avoid stale closures,
  // uses chained setTimeout so each cycle completes before the next starts.
  useEffect(() => {
    const hasExtracting = items.some((i) => i.status === "extracting");
    if (!hasExtracting) {
      pollingRef.current = false;
      return;
    }
    if (pollingRef.current) return;
    pollingRef.current = true;

    let cancelled = false;

    async function pollOnce() {
      if (cancelled) return;

      // Read latest items from ref (never stale)
      const current = itemsRef.current;
      const extracting = current.filter(
        (i) => i.status === "extracting" && i.result
      );

      if (extracting.length === 0) {
        pollingRef.current = false;
        return;
      }

      // Poll all extracting items in parallel
      const results = await Promise.allSettled(
        extracting.map(async (item) => {
          const status = await getRiderStatus(item.result!.rider_id);
          return { item, status };
        })
      );

      if (cancelled) return;

      // Batch all updates into a single setItems call
      setItems((prev) => {
        let changed = false;
        const next = prev.map((i) => {
          const match = results.find(
            (r) =>
              r.status === "fulfilled" && r.value.item.id === i.id
          );
          if (!match || match.status !== "fulfilled") return i;

          const { status } = match.value;
          if (
            status.status === "extracted" ||
            status.status === "approved"
          ) {
            changed = true;
            return {
              ...i,
              status: "extracted" as const,
              extractionStep: undefined,
            };
          } else if (status.status === "failed") {
            changed = true;
            return {
              ...i,
              status: "failed" as const,
              error: status.error || "Extraction failed",
              extractionStep: undefined,
            };
          } else if (
            status.status === "extracting" &&
            status.step &&
            status.step !== i.extractionStep
          ) {
            changed = true;
            return { ...i, extractionStep: status.step };
          }
          return i;
        });
        if (!changed) return prev;
        return next;
      });

      // Invalidate riders cache if any completed
      const anyCompleted = results.some(
        (r) =>
          r.status === "fulfilled" &&
          (r.value.status.status === "extracted" ||
            r.value.status.status === "approved" ||
            r.value.status.status === "failed")
      );
      if (anyCompleted) {
        queryClient.invalidateQueries({ queryKey: ["riders"] });
      }

      // Schedule next poll
      if (!cancelled) {
        setTimeout(pollOnce, 2000);
      }
    }

    // Start first poll after a short delay
    const timer = setTimeout(pollOnce, 1500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      pollingRef.current = false;
    };
  }, [items, queryClient]);

  const addFiles = async (files: FileList) => {
    if (quotaExhausted) {
      toast.error(QUOTA_GATE_TOAST, { id: "upload-quota-gate" });
      return;
    }
    const pdfs = Array.from(files).filter(
      (f) => f.type === "application/pdf" || f.name.endsWith(".pdf")
    );

    const currentStaged = items.filter((i) => i.status === "staged").length;
    const remaining = maxBatch - currentStaged;

    const uploadsWord = maxBatch === 1 ? "upload" : "uploads";
    if (remaining <= 0) {
      toast.error(
        batchClampedByQuota
          ? `Only ${maxBatch} ${uploadsWord} left this month`
          : `Batch limit reached (${maxBatch} files max)`,
      );
      return;
    }

    const toAdd = pdfs.slice(0, remaining);
    if (toAdd.length < pdfs.length) {
      toast(
        batchClampedByQuota
          ? `Only ${toAdd.length} of ${pdfs.length} files added — ${maxBatch} ${uploadsWord} left this month`
          : `Only ${toAdd.length} of ${pdfs.length} files added — batch limit is ${maxBatch}`,
      );
    }

    const existingHashes = new Set(
      (existingRiders ?? [])
        .map((r) => r.content_sha256)
        .filter((hash): hash is string => Boolean(hash)),
    );
    const stagedHashes = new Set(
      items
        .map((item) => item.contentHash)
        .filter((hash): hash is string => Boolean(hash)),
    );
    const skipped: string[] = [];
    const newItems: UploadItem[] = [];

    for (const file of toAdd) {
      const hash = await fileSha256(file);
      if (hash && (existingHashes.has(hash) || stagedHashes.has(hash))) {
        skipped.push(file.name);
        continue;
      }
      if (hash) stagedHashes.add(hash);
      newItems.push({
        id: String(++nextId),
        file,
        artistName: file.name.replace(/\.pdf$/i, ""),
        fileName: file.name,
        contentHash: hash,
        status: "staged" as const,
      });
    }

    if (skipped.length > 0) {
      toast.warning(
        `${skipped.length} duplicate ${skipped.length === 1 ? "PDF was" : "PDFs were"} skipped`,
      );
    }
    if (newItems.length === 0) return;
    setItems((prev) => [...prev, ...newItems]);
  };

  const updateName = (id: string, name: string) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, artistName: name } : i)));
  };

  const removeStaged = (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const startUpload = (itemsToUpload: UploadItem[]) => {
    // Bound concurrency: at most 3 uploads in flight so a big batch doesn't
    // fire N parallel requests and exhaust the API's small DB connection pool.
    // Free uploads one at a time: its cap is hard and the server's count check
    // has no lock, so parallel uploads could overshoot it.
    void mapLimit(itemsToUpload, uploadConcurrency(quota, isAdmin), async (item) => {
      setItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, status: "uploading" } : i))
      );
      try {
        const result = await uploadRider(
          item.file,
          item.artistName.trim() || undefined
        );
        setItems((prev) =>
          prev.map((i) =>
            i.id === item.id ? { ...i, status: "extracting", result } : i
          )
        );
        queryClient.invalidateQueries({ queryKey: ["riders"] });
        queryClient.invalidateQueries({ queryKey: ["upload-quota"] });
        queryClient.invalidateQueries({ queryKey: ONBOARDING_PROGRESS_QUERY_KEY });
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed";
        const isQuotaExceeded = isQuotaError(msg);
        setItems((prev) =>
          prev.map((i) =>
            i.id === item.id
              ? { ...i, status: "error", error: isQuotaExceeded ? QUOTA_ITEM_ERROR : msg }
              : i
          )
        );
        if (isQuotaExceeded) {
          // One toast per batch even when several parallel uploads hit the wall.
          toast.error(QUOTA_TOAST, { id: "upload-quota-exceeded" });
        } else {
          toast.error(`Upload failed: ${msg}`);
        }
        // Refresh the quota so the pre-gate is in place when the user comes
        // back to staging via "Upload more".
        queryClient.invalidateQueries({ queryKey: ["upload-quota"] });
      }
    });
  };

  const uploadAll = () => {
    const staged = items.filter((i) => i.status === "staged");
    if (staged.length === 0) return;

    // Transition staging → processing
    const stagingEl = stagingRef.current;
    if (stagingEl) {
      anime({
        targets: stagingEl,
        opacity: [1, 0],
        translateY: [0, -20],
        easing: "easeInQuart",
        duration: 350,
        complete: () => {
          setShowStaging(false);
          setShowProcessing(true);

          // Start uploads after transition
          startUpload(staged);

          // Animate processing view in
          requestAnimationFrame(() => {
            const procEl = processingRef.current;
            if (procEl) {
              anime({
                targets: procEl,
                opacity: [0, 1],
                translateY: [30, 0],
                easing: "easeOutCubic",
                duration: 500,
              });
            }
          });
        },
      });
    } else {
      // Fallback: no animation
      setShowStaging(false);
      setShowProcessing(true);
      startUpload(staged);
    }
  };

  const handleRetry = useCallback((itemId: string) => {
    setItems((prev) => {
      const item = prev.find((i) => i.id === itemId);
      if (!item) return prev;

      // Reset status, re-upload
      const updated = prev.map((i) =>
        i.id === itemId ? { ...i, status: "uploading" as const, error: undefined, result: undefined, extractionStep: undefined } : i
      );

      // Start upload for this single item
      uploadRider(item.file, item.artistName.trim() || undefined)
        .then((result) => {
          setItems((p) =>
            p.map((i) =>
              i.id === itemId ? { ...i, status: "extracting", result } : i
            )
          );
          queryClient.invalidateQueries({ queryKey: ["riders"] });
        })
        .catch((err) => {
          const msg = err instanceof Error ? err.message : "Failed";
          const isQuotaExceeded = isQuotaError(msg);
          setItems((p) =>
            p.map((i) =>
              i.id === itemId
                ? { ...i, status: "error", error: isQuotaExceeded ? QUOTA_ITEM_ERROR : msg }
                : i
            )
          );
          if (isQuotaExceeded) {
            toast.error(QUOTA_TOAST, { id: "upload-quota-exceeded" });
          }
          queryClient.invalidateQueries({ queryKey: ["upload-quota"] });
        });

      return updated;
    });
  }, [queryClient]);

  const handleRetryAllFailed = useCallback(() => {
    const failed = items.filter((i) => i.status === "failed" || i.status === "error");
    for (const item of failed) {
      handleRetry(item.id);
    }
  }, [items, handleRetry]);

  const handleUploadMore = useCallback(() => {
    // Reset to staging phase
    setItems([]);
    setShowProcessing(false);
    setShowStaging(true);
  }, []);

  const handleImportUrl = async () => {
    const url = importUrl.trim();
    if (!url) return;
    if (quotaExhausted) {
      toast.error(QUOTA_GATE_TOAST, { id: "upload-quota-gate" });
      return;
    }

    setImportLoading(true);
    try {
      const result = await importRiderFromUrl(url, importArtistName.trim() || undefined);

      // Add to items as extracting (same as file upload post-upload)
      const id = String(++nextId);
      const newItem: UploadItem = {
        id,
        file: new File([], "imported.pdf"), // placeholder
        artistName: result.artist_name,
        fileName: url.split("/").pop() || "imported.pdf",
        status: "extracting",
        result,
      };

      setItems((prev) => [...prev, newItem]);
      setImportUrl("");
      setImportArtistName("");

      // Transition to processing view
      setShowStaging(false);
      setShowProcessing(true);

      // Animate processing view in (same as uploadAll)
      requestAnimationFrame(() => {
        const procEl = processingRef.current;
        if (procEl) {
          anime({
            targets: procEl,
            opacity: [0, 1],
            translateY: [30, 0],
            easing: "easeOutCubic",
            duration: 500,
          });
        }
      });

      queryClient.invalidateQueries({ queryKey: ["riders"] });
      queryClient.invalidateQueries({ queryKey: ["upload-quota"] });
      queryClient.invalidateQueries({ queryKey: ONBOARDING_PROGRESS_QUERY_KEY });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Import failed";
      toast.error(isQuotaError(msg) ? QUOTA_TOAST : msg);
      queryClient.invalidateQueries({ queryKey: ["upload-quota"] });
    } finally {
      setImportLoading(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (quotaExhausted) {
      toast.error(QUOTA_GATE_TOAST, { id: "upload-quota-gate" });
      return;
    }

    // Handle folder drops via webkitGetAsEntry
    const entries = Array.from(e.dataTransfer.items)
      .map((item) => item.webkitGetAsEntry?.())
      .filter(Boolean) as FileSystemEntry[];

    if (entries.some((entry) => entry.isDirectory)) {
      const files: File[] = [];
      for (const entry of entries) {
        if (entry.isDirectory) {
          await readDirectory(entry as FileSystemDirectoryEntry, files);
        } else if (entry.isFile) {
          const file = await getFile(entry as FileSystemFileEntry);
          if (file.name.toLowerCase().endsWith(".pdf")) files.push(file);
        }
      }
      if (files.length > 0) {
        const dt = new DataTransfer();
        files.forEach((f) => dt.items.add(f));
        await addFiles(dt.files);
      }
    } else if (e.dataTransfer.files.length) {
      await addFiles(e.dataTransfer.files);
    }
  };

  const readDirectory = (dir: FileSystemDirectoryEntry, files: File[]): Promise<void> => {
    return new Promise((resolve) => {
      const reader = dir.createReader();
      const readEntries = () => {
        reader.readEntries(async (entries) => {
          if (entries.length === 0) { resolve(); return; }
          for (const entry of entries) {
            if (entry.isFile && entry.name.toLowerCase().endsWith(".pdf")) {
              files.push(await getFile(entry as FileSystemFileEntry));
            } else if (entry.isDirectory) {
              await readDirectory(entry as FileSystemDirectoryEntry, files);
            }
          }
          readEntries();
        });
      };
      readEntries();
    });
  };

  const getFile = (entry: FileSystemFileEntry): Promise<File> => {
    return new Promise((resolve, reject) => entry.file(resolve, reject));
  };

  const stagedCount = items.filter((i) => i.status === "staged").length;

  // Mirrors GatedFeature.handleUpgrade: Stripe checkout for Explorer.
  const handleUpgrade = async () => {
    setUpgradeLoading(true);
    try {
      const { checkout_url } = await createCheckoutSession("explorer");
      window.location.href = checkout_url;
    } catch (err) {
      setUpgradeLoading(false);
      toast.error(err instanceof Error ? err.message : "Could not open checkout");
    }
  };

  return (
    <div className="space-y-6">
      <RidersWorkspaceHeader busy={importLoading || showProcessing} />

      {/* Staging phase: drop zone + how-it-works + file list */}
      {showStaging && (
        <div data-tour="rider-upload-queue" ref={stagingRef} style={{ willChange: "transform, opacity" }} className="space-y-6">
          {/* Free-tier pre-gate: the monthly allowance is used up */}
          {quotaExhausted && quota && (
            <div
              role="status"
              className="card flex flex-col gap-4 border-accent/40 p-5 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex items-start gap-3">
                <div
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-ds-sm text-accent"
                  style={{ background: "var(--tr-accent-soft)" }}
                >
                  <Lock className="h-4 w-4" strokeWidth={1.6} />
                </div>
                <div className="min-w-0">
                  <p className="text-[13.5px] font-bold text-ink">
                    You've used your {quota.limit} free riders this month
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-ink-3">
                    Your allowance renews on the 1st. Explorer gives you 50 riders a month, 3 festivals and CSV export.
                  </p>
                </div>
              </div>
              <Button
                variant="accent"
                icon={upgradeLoading ? undefined : Zap}
                onClick={handleUpgrade}
                disabled={upgradeLoading}
                className="shrink-0"
              >
                {upgradeLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Upgrade to Explorer
              </Button>
            </div>
          )}

          <div className="grid items-start gap-5 lg:grid-cols-2">
            {/* Drop zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); if (!quotaExhausted) setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`card relative flex flex-col items-center justify-center overflow-hidden p-12 text-center transition-colors ${
                dragOver ? "border-accent" : quotaExhausted ? "opacity-60" : "hover:border-line-3"
              }`}
              style={
                dragOver
                  ? { background: "color-mix(in oklab, var(--tr-accent) 6%, var(--tr-panel))" }
                  : undefined
              }
            >
              {/* Soft accent glow at the top */}
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "radial-gradient(circle at 50% 0%, var(--tr-accent-soft), transparent 60%)",
                  opacity: 0.6,
                }}
              />
              <div
                className="relative grid h-14 w-14 place-items-center rounded-ds-sm text-accent"
                style={{ background: "var(--tr-accent-soft)" }}
              >
                <Upload className="h-6 w-6" strokeWidth={1.6} />
              </div>
              <p className="relative mt-4 text-base font-bold text-ink">
                Drop rider PDFs here
              </p>
              <p className="relative mt-1 text-sm text-ink-3">
                or browse files and folders from your computer
              </p>
              {/* DA SISTEMARE (redesign): the handoff shows PDF/DOCX/TXT chips,
                  but ingestion only accepts PDF today (accept=".pdf" + the drop
                  handler filters to .pdf). Showing PDF only to avoid implying
                  unsupported formats; restore DOCX/TXT when ingestion supports them. */}
              <div className="relative mt-4 inline-flex gap-1.5">
                {["PDF"].map((fmt) => (
                  <span
                    key={fmt}
                    className="rounded-ds-xs border border-line-2 px-2 py-0.5 font-mono text-[10.5px] text-ink-4"
                  >
                    {fmt}
                  </span>
                ))}
              </div>
              <div data-tour="rider-source-picker" className="relative mt-5 flex flex-wrap items-center gap-2">
                <label
                  className={`btn btn--accent ${quotaExhausted ? "pointer-events-none opacity-50" : "cursor-pointer"}`}
                >
                  <Upload className="h-3.5 w-3.5" strokeWidth={1.6} />
                  Browse files
                  <input
                    ref={pdfPickerRef}
                    type="file"
                    accept=".pdf"
                    multiple
                    disabled={quotaExhausted}
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files) void addFiles(e.target.files);
                    }}
                  />
                </label>
                <label
                  className={`btn btn--ghost ${quotaExhausted ? "pointer-events-none opacity-50" : "cursor-pointer"}`}
                >
                  <FolderOpen className="h-3.5 w-3.5" strokeWidth={1.6} />
                  Browse folder
                  <input
                    type="file"
                    // @ts-expect-error webkitdirectory is not in React types
                    webkitdirectory=""
                    directory=""
                    multiple
                    disabled={quotaExhausted}
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files) void addFiles(e.target.files);
                    }}
                  />
                </label>
                {demoActive && (
                  <Button
                    variant="ghost"
                    disabled={quotaExhausted}
                    onClick={() => {
                      const picker = pdfPickerRef.current;
                      if (!picker) return;
                      const files = new DataTransfer();
                      files.items.add(new File([createDemoPdf("Trace Demo DJ Technical Rider", [
                        "Artist: Demo Touring DJ", "Players: 3 x Pioneer CDJ-3000",
                        "Mixer: 1 x Pioneer DJM-900NXS2", "Monitor: 2 x booth wedges",
                        "Demo source document for the guided extraction workflow.",
                      ])], "trace-demo-dj-rider.pdf", { type: "application/pdf" }));
                      picker.files = files.files;
                      picker.dispatchEvent(new Event("change", { bubbles: true }));
                    }}
                  >
                    Use demo PDF
                  </Button>
                )}
              </div>
            </div>

            {/* How extraction works */}
            <div className="card p-5">
              <h3 className="mb-3.5 text-[13.5px] font-bold text-ink">
                How extraction works
              </h3>
              <div className="space-y-1">
                {EXTRACTION_STEPS.map((step, i) => (
                  <div key={step.title} className="flex gap-3 py-2.5">
                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-ds-xs bg-panel-3 font-mono text-xs font-semibold text-ink-2">
                      {i + 1}
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 text-[13.5px] font-semibold text-ink">
                        <Icon icon={step.icon} size={14} className="text-ink-3" />
                        {step.title}
                      </div>
                      <p className="mt-0.5 text-xs leading-relaxed text-ink-3">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* URL import */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-line-2" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-bg px-3 text-ink-3">or import from URL</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="relative flex-1">
                <LinkIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" strokeWidth={1.6} />
                <Input
                  value={importUrl}
                  onChange={(e) => setImportUrl(e.target.value)}
                  placeholder="https://drive.google.com/file/d/… or Dropbox link"
                  className="pl-9"
                  onKeyDown={(e) => { if (e.key === "Enter" && importUrl.trim()) handleImportUrl(); }}
                />
              </div>
              <Input
                value={importArtistName}
                onChange={(e) => setImportArtistName(e.target.value)}
                placeholder="Artist name (optional)"
                className="sm:w-48"
              />
              <Button
                variant="accent"
                icon={importLoading ? undefined : LinkIcon}
                onClick={handleImportUrl}
                disabled={!importUrl.trim() || importLoading || quotaExhausted}
                title={quotaExhausted ? "Monthly limit reached" : undefined}
              >
                {importLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Import
              </Button>
            </div>
            <p className="text-[11px] text-ink-4">
              Supports Google Drive and Dropbox sharing links. The PDF will be downloaded and processed server-side.
            </p>
          </div>

          {/* Batch counter + monthly quota */}
          <div className="flex items-center justify-between gap-3 font-mono text-[11px] text-ink-3">
            <span>
              {stagedCount} / {maxBatch} files in batch
            </span>
            {quota && (
              quota.limit == null ? (
                <span>Unlimited uploads</span>
              ) : quota.tier === "free" ? (
                <span
                  className={`font-sans text-xs font-semibold ${
                    (quota.remaining ?? 0) <= 0 ? "text-warn" : "text-ink"
                  }`}
                >
                  {Math.max(0, quota.remaining ?? 0)} of {quota.limit} free riders left this month
                </span>
              ) : (
                <span>
                  {`${quota.remaining ?? 0} uploads remaining this month (${quota.used}/${quota.limit})`}
                </span>
              )
            )}
          </div>

          {/* Staged file list */}
          {items.length > 0 && (
            <div className="space-y-3">
              <SectionHeader icon={Inbox} title="Files" count={items.length} />
              {items.map((item) => {
                const dup = findDuplicate(item);
                return (
                  <div
                    key={item.id}
                    className="card flex items-center gap-3 px-4 py-3"
                  >
                    <FileText className="h-4 w-4 shrink-0 text-ink-3" strokeWidth={1.6} />
                    <div className="min-w-0 flex-1">
                      <div>
                        <div className="flex items-center gap-2">
                          <input
                            value={item.artistName}
                            onChange={(e) => updateName(item.id, e.target.value)}
                            className="input"
                            style={
                              dup
                                ? {
                                    borderColor: "color-mix(in oklab, var(--tr-warn) 55%, transparent)",
                                  }
                                : undefined
                            }
                            placeholder="Artist name"
                          />
                          <Pencil className="h-3 w-3 shrink-0 text-ink-4" strokeWidth={1.6} />
                        </div>
                        {dup && (
                          <p className="mt-1 flex items-center gap-1 text-[11px] text-warn">
                            <AlertTriangle className="h-3 w-3 shrink-0" strokeWidth={1.6} />
                            {dup.kind === "filename"
                              ? `Filename "${dup.label}" already exists`
                              : `A rider for "${dup.label}" already exists`}
                          </p>
                        )}
                      </div>
                      <p className="mt-0.5 truncate font-mono text-[11px] text-ink-4">{item.file.name}</p>
                    </div>
                    <button
                      onClick={() => removeStaged(item.id)}
                      className="shrink-0 rounded-ds-xs p-1 text-ink-4 transition-colors hover:bg-panel-2 hover:text-ink-2"
                      title="Remove"
                      aria-label="Remove file"
                    >
                      <X className="h-3.5 w-3.5" strokeWidth={1.6} />
                    </button>
                  </div>
                );
              })}

              {stagedCount > 0 && (
                <Button variant="accent" className="w-full" onClick={uploadAll}>
                  Upload {stagedCount} {stagedCount === 1 ? "file" : "files"}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Processing phase */}
      {showProcessing && (
        <div
          data-tour="rider-processing"
          ref={processingRef}
          style={{ opacity: 0, willChange: "transform, opacity" }}
        >
          <ProcessingView
            items={items}
            onRetry={handleRetry}
            onRetryAllFailed={handleRetryAllFailed}
            onUploadMore={handleUploadMore}
          />
        </div>
      )}
    </div>
  );
}
